<script setup lang="ts">
import { reactive, ref } from "vue";
import { useResponseError } from "~/composables/error";

const { $api } = useNuxtApp();
const { t } = useI18n();
const toast = useToast();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();

const open = ref(false);

const form = reactive({
  oldPassword: "",
  newPassword: "",
  rePassword: "",
});
const saveLoading = ref(false);

function reset() {
  form.oldPassword = "";
  form.newPassword = "";
  form.rePassword = "";
  clearErrors();
  saveLoading.value = false;
}

function openModal() {
  reset();
  open.value = true;
}

function close() {
  open.value = false;
  reset();
}

async function submit() {
  clearErrors();

  if (form.newPassword !== form.rePassword) {
    formErrors.value = { rePassword: t("profile.changePassword.mismatch") };
    return;
  }

  saveLoading.value = true;
  try {
    await $api("/profile/password", {
      method: "POST",
      body: {
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
        rePassword: form.rePassword,
      },
    });
    toast.success({ title: t("profile.changePassword.success") });
    close();
  } catch (e: any) {
    setErrors(e);
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  } finally {
    saveLoading.value = false;
  }
}

defineExpose({
  open: openModal,
  close,
});
</script>

<template>
  <Modal
    :open="open"
    :title="t('profile.changePassword.title')"
    size="sm"
    @update:open="(v: boolean) => (open = v)"
    @close="close"
  >
    <form
      class="flex flex-col gap-medium"
      autocomplete="off"
      data-testid="change-password-form"
      @submit.prevent="submit"
    >
      <FormField
        v-model="form.oldPassword"
        type="password"
        name="profile-old-password"
        autocomplete="current-password"
        :label="t('field.currentPassword')"
        :error="formErrors.oldPassword"
      />

      <FormField
        v-model="form.newPassword"
        type="password"
        name="profile-new-password"
        autocomplete="new-password"
        :label="t('field.newPassword')"
        :error="formErrors.newPassword"
      />

      <FormField
        v-model="form.rePassword"
        type="password"
        name="profile-confirm-password"
        autocomplete="new-password"
        :label="t('field.confirmPassword')"
        :error="formErrors.rePassword"
      />
    </form>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="small"
        :disabled="saveLoading"
        data-testid="change-password-save"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
